import { publicPath } from '@utils/public-path';
import type { StrongsDefinition } from './types';

const CACHE_PREFIX = 'strongs:';

/**
 * Fetches a Strong's definition by number (e.g., "G1161" or "H7225").
 * Uses localStorage for caching with a lazy, cache-as-used approach.
 */
export async function fetchStrongsDefinition(
  strongsNumber: string,
): Promise<StrongsDefinition | null> {
  const cacheKey = CACHE_PREFIX + strongsNumber;

  // Check localStorage cache first
  const cached = localStorage.getItem(cacheKey);
  if (cached) {
    try {
      return JSON.parse(cached) as StrongsDefinition;
    } catch {
      // Invalid cache, remove it
      localStorage.removeItem(cacheKey);
    }
  }

  // Fetch from static file
  try {
    const response = await fetch(publicPath(`strongs/${strongsNumber}.json`));
    if (!response.ok) {
      console.warn(`Strongs definition not found: ${strongsNumber}`);
      return null;
    }

    const data = (await response.json()) as StrongsDefinition;

    // Cache in localStorage
    try {
      localStorage.setItem(cacheKey, JSON.stringify(data));
    } catch (e) {
      // localStorage might be full - try to clear old entries
      clearOldCacheEntries(CACHE_PREFIX);
      try {
        localStorage.setItem(cacheKey, JSON.stringify(data));
      } catch {
        // Still failed, just continue without caching
        console.warn('Failed to cache strongs definition');
      }
    }

    return data;
  } catch (error) {
    console.error(`Error fetching strongs definition: ${strongsNumber}`, error);
    return null;
  }
}

/**
 * Fetches multiple Strong's definitions in parallel.
 */
export async function fetchMultipleStrongs(
  numbers: string[],
): Promise<(StrongsDefinition | null)[]> {
  return Promise.all(numbers.map((n) => fetchStrongsDefinition(n)));
}

/**
 * Clears old cache entries to make room for new ones.
 * Removes entries with the given prefix, oldest first.
 */
function clearOldCacheEntries(prefix: string): void {
  const keysToRemove: string[] = [];

  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key?.startsWith(prefix)) {
      keysToRemove.push(key);
    }
  }

  // Remove half of the cached entries
  const removeCount = Math.ceil(keysToRemove.length / 2);
  for (let i = 0; i < removeCount; i++) {
    localStorage.removeItem(keysToRemove[i]);
  }
}
